import {
  Button,
  Cluster,
  Diagnostic,
  Icon,
  IconButton,
  Kicker,
  Note,
} from "@fwf/fwfa-design-system/react";
import type { PermissionRequest } from "@/types";
import {
  isAllowPermissionOption,
  isRejectPermissionOption,
  pickAutoApproveOption,
} from "@/lib/permission-presets";
import { humanizeToolName } from "@/lib/tool-display";

interface Props {
  request: PermissionRequest;
  onResponse: (optionId: string | null) => void;
  onAlwaysApprove: () => void;
}

const CANCEL_OPTION_PATTERN = /\b(cancel|dismiss|close|not now|skip)\b/i;
const APPROVE_OPTION_PATTERN = /\b(allow|approve|continue|yes|ok|okay|proceed|accept)\b/i;
const REJECT_OPTION_PATTERN = /\b(reject|deny|decline|no|block|feedback)\b/i;

type OptionTone = "approve" | "reject" | "neutral";

function getOptionTone(optionName: string, isAllow: boolean, isReject: boolean): OptionTone {
  if (isReject || REJECT_OPTION_PATTERN.test(optionName)) return "reject";
  if (isAllow || APPROVE_OPTION_PATTERN.test(optionName)) return "approve";
  if (CANCEL_OPTION_PATTERN.test(optionName)) return "neutral";
  return "neutral";
}

function getOptionLabel(
  optionName: string,
  optionKind: string,
  tone: OptionTone,
  isQuestionRequest: boolean,
) {
  if (optionKind === "allow_always") return "Approve";
  if (optionKind === "reject_always") return "Always deny";
  if (isQuestionRequest) {
    if (tone === "approve") return "OK";
    if (tone === "reject") return "Dismiss";
  }
  if (tone === "approve") return "Approve";
  if (tone === "reject") return "Deny";
  if (CANCEL_OPTION_PATTERN.test(optionName)) return "Cancel";
  return optionName.trim();
}

// Approve is the neutral solid, never a brand fill: DS `primary` is a white block
// on dark / navy on light, so the accent stays spent.
//
// `danger` is spent on exactly one thing: `reject_always`. A one-off deny is the
// conservative, repeatable answer — the agent simply asks again — so it takes the
// quiet outline, and answering a question destroys nothing at all. Painting either
// of those orange would spend the destructive register on the safe choice and
// leave nothing to mark the one decision that actually persists.
function getOptionVariant(tone: OptionTone, optionKind: string, isQuestionRequest: boolean) {
  if (tone === "approve") return "primary" as const;
  if (isQuestionRequest) return "ghost" as const;
  if (optionKind === "reject_always") return "danger" as const;
  if (tone === "reject") return "secondary" as const;
  return "ghost" as const;
}

export function PermissionDialog({ request, onResponse, onAlwaysApprove }: Props) {
  const hasTechnicalDetails = !!request.args && Object.keys(request.args).length > 0;
  const autoApproveOption = request.options.length > 0
    ? pickAutoApproveOption(request.options)
    : null;
  const hasNonPersistentApproveOption = request.options.some((option) =>
    option.kind === "allow_once" || option.kind === "approve" || option.kind === "allow",
  );
  const visibleOptions = request.options.filter((option) => {
    if (option.kind !== "allow_always") return true;
    return !hasNonPersistentApproveOption;
  });
  const askUserPattern = /^(asking user:|ask user:)\s*/i;
  const isQuestionRequest = askUserPattern.test(request.toolName) || askUserPattern.test(request.description || "");
  const normalizedToolName = request.toolName.replace(askUserPattern, "").trim();
  const normalizedDescription = (request.description || "").replace(askUserPattern, "").trim();
  const friendlyToolName = humanizeToolName(normalizedToolName || request.toolName);
  const primaryText = isQuestionRequest
    ? normalizedDescription || normalizedToolName
    : friendlyToolName;
  const secondaryText = !isQuestionRequest && normalizedDescription && normalizedDescription !== normalizedToolName
    ? normalizedDescription
    : null;
  const labelId = `permission-${request.id}-label`;

  return (
    // Note carries the whole tone: its masked glyph is the warning triangle for a
    // permission request and the info mark for a question, so neither icon needs
    // drawing here and neither variant needs its own tint. That is also the single
    // yellow this card spends — the old one spent it six times over (border, wash,
    // icon chip, heading, approve fill, and the always-approve link).
    <Note
      variant={isQuestionRequest ? "info" : "warn"}
      role="group"
      aria-labelledby={labelId}
      // pr-14 clears the corner dismiss control: the button is 44px on a phone
      // plus its 4px inset, so anything less lets a long line run under it.
      className="animate-scale-in flex w-full flex-col gap-3 pr-14"
    >
      {/* The dismiss control sits in the corner rather than in a row with the
          label. Sharing a flex row couples the label's baseline to the button's
          box, and that box is 44px on a phone (the tap-height floor) against a
          17px eyebrow, which drops the cross 13.5px below the text it reads as
          being beside. Note is position:relative, so the corner is free. */}
      <IconButton
        label="Dismiss permission request"
        compact
        title="Dismiss"
        onClick={() => onResponse(null)}
        className="absolute right-1 top-1"
      >
        <Icon aria-hidden="true">
          <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </Icon>
      </IconButton>

      {/* mb-0 clears the Kicker's own bottom margin, which is rhythm for a
          Kicker-over-Title pair and doubles up with this card's flex gap.
          Tailwind utilities are unlayered, so they win over the fwfa layers. */}
      <Kicker id={labelId} className="mb-0">
        {isQuestionRequest ? "Input needed" : "Permission request"}
      </Kicker>

      <div className="flex flex-col gap-1">
        <p className="font-medium text-ink">{primaryText}</p>
        {secondaryText && <p className="text-xs text-ink-muted">{secondaryText}</p>}
      </div>

      {visibleOptions.length > 0 ? (
        <Cluster className="gap-2">
          {visibleOptions.map((option) => {
            const isReject = isRejectPermissionOption(option);
            const isAllow = isAllowPermissionOption(option);
            const tone = getOptionTone(option.name, isAllow, isReject);

            return (
              <Button
                key={option.optionId}
                variant={getOptionVariant(tone, option.kind, isQuestionRequest)}
                compact
                onClick={() => onResponse(option.optionId)}
              >
                {getOptionLabel(option.name, option.kind, tone, isQuestionRequest)}
              </Button>
            );
          })}
        </Cluster>
      ) : (
        /* No options provided — cancelling is the only safe action since
           sending a fabricated optionId could crash the agent */
        <p className="text-xs text-ink-muted">Dismiss this prompt with the close button.</p>
      )}

      {!isQuestionRequest && autoApproveOption && (
        <Cluster>
          <Button variant="ghost" compact onClick={onAlwaysApprove}>
            Always approve this tool
          </Button>
        </Cluster>
      )}

      {/* Diagnostic's own `summary` disclosure replaces the hand-rolled open/closed
          state, the rotating chevron, and the bespoke <pre>. It also caps its own
          height and scrolls, which the old block did not. */}
      {hasTechnicalDetails && (
        <Diagnostic summary="Technical details">
          {JSON.stringify(request.args, null, 2)}
        </Diagnostic>
      )}
    </Note>
  );
}
